import _uniq from 'lodash/uniq';

import { FLOW_STEP_PATHS } from '@/constants';

import Applicant from '@/containers/Applicant';
import Ceo from '@/containers/Ceo';
import CompanyDetails from '@/containers/CompanyDetails';
import UltimateBeneficialOwner from '@/containers/UltimateBeneficialOwner';
import { STAKEHOLDER_ROLES_V2 } from '@/containers/withStakeholderLogic';

import { SoleTraderBlock } from '@/flow/blocks/kyc/common/SoleTrader.block';
import { PartnershipBlock } from '@/flow/blocks/kyc/common/Partnership.block';

import { or, not } from '@/flow/common';
import {
  hasCompanyDetails,
  hasControlOverManagementStakeholders,
  hasMaxUBOsWithMultipleRoles,
  hasPartnerRole,
  hasPartnershipDetails,
  hasSubmittedStakeholderBlockFca,
  hasUltimateBeneficialOwner,
  isEntityTypePartnership,
  isEntityTypePrivateCompany,
  isEntityTypePublicListedCompany,
  isMerchantRegistered,
  isSoleTraderOrSoleProprietorship,
} from '@/flow/configs/selectors';

export class FcaStakeholderBlock {
  constructor({ isManagedFlow, countryConfig, prerequisite = [] }) {
    this.isManagedFlow = isManagedFlow;
    this.countryConfig = countryConfig;
    this.prerequisite = prerequisite;
  }

  static selectors = {
    hasCompanyDetails,
    hasControlOverManagementStakeholders,
    hasMaxUBOsWithMultipleRoles,
    hasPartnerRole,
    hasPartnershipDetails,
    hasSubmittedStakeholderBlockFca,
    hasUltimateBeneficialOwner,
    isEntityTypePartnership,
    isEntityTypePrivateCompany,
    isEntityTypePublicListedCompany,
    isMerchantRegistered,
    isSoleTraderOrSoleProprietorship,
    ...SoleTraderBlock.selectors,
    ...PartnershipBlock.selectors,
  };

  getSelectors() {
    return FcaStakeholderBlock.selectors;
  }

  getSelectorToSubmitStep() {
    return 'hasSubmittedStakeholderBlockFca';
  }

  generateNextForPreviousStep() {
    return [
      {
        condition: 'isSoleTraderOrSoleProprietorship',
        path: FLOW_STEP_PATHS.SOLE_TRADER_PATH,
      },
      {
        condition: 'isEntityTypePartnership',
        path: FLOW_STEP_PATHS.PARTNERSHIP_DETAILS_PATH,
      },
      ...(this.isManagedFlow || this.countryConfig?.COLLECT_COMPANY_DETAILS_ON_UNMANAGED
        ? [
            {
              condition: 'isMerchantRegistered',
              path: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
            },
          ]
        : []),
    ];
  }

  generateBackForNextStep() {
    return [
      {
        condition: 'isSoleTraderOrSoleProprietorship',
        path: FLOW_STEP_PATHS.SOLE_TRADER_PATH,
      },
      {
        condition: true,
        path: FLOW_STEP_PATHS.APPLICANT_PATH,
      },
    ];
  }

  withGlobalPrerequisites(prerequisite = []) {
    return _uniq([...prerequisite, ...(this.prerequisite || [])]);
  }

  generateFlowConfig({ next, back }) {
    const soleTraderBlock = new SoleTraderBlock({
      isManagedFlow: this.isManagedFlow,
      prerequisite: this.prerequisite,
      back,
      next,
    });
    const partnershipBlock = new PartnershipBlock({
      isManagedFlow: this.isManagedFlow,
      prerequisite: this.prerequisite,
      back,
      next: FLOW_STEP_PATHS.ULTIMATE_BENEFICIAL_OWNER_PATH,
    });

    return [
      ...partnershipBlock.generateFlowConfig(),
      ...soleTraderBlock.generateFlowConfig(),
      {
        id: 'company-details',
        path: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
        prerequisite: this.withGlobalPrerequisites([
          'isMerchantRegistered',
          ...(this.isManagedFlow
            ? [or(['isEntityTypePrivateCompany', 'isEntityTypePublicListedCompany'])]
            : []),
        ]),
        Container: CompanyDetails,
        back,
        next: this.isManagedFlow
          ? [
              {
                condition: 'isEntityTypePublicListedCompany',
                path: FLOW_STEP_PATHS.APPLICANT_PATH,
              },
              {
                condition: 'isEntityTypePrivateCompany',
                path: FLOW_STEP_PATHS.ULTIMATE_BENEFICIAL_OWNER_PATH,
              },
            ]
          : FLOW_STEP_PATHS.ULTIMATE_BENEFICIAL_OWNER_PATH,
      },
      {
        id: 'ultimate-beneficial-owner',
        path: FLOW_STEP_PATHS.ULTIMATE_BENEFICIAL_OWNER_PATH,
        prerequisite: this.withGlobalPrerequisites([
          or(
            ['isEntityTypePartnership', 'hasPartnershipDetails', 'hasPartnerRole'],
            ['isMerchantRegistered', 'hasCompanyDetails'],
          ),
        ]),
        Container: UltimateBeneficialOwner,
        skip: 'hasMaxUBOsWithMultipleRoles',
        back: [
          {
            condition: 'isMerchantRegistered',
            path: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
          },
          {
            condition: true,
            path: partnershipBlock.getBackPathForNextStep(),
          },
        ],
        next: [
          {
            condition: [not('hasUltimateBeneficialOwner'), 'isMerchantRegistered'],
            path: FLOW_STEP_PATHS.ARTIFICIAL_ULTIMATE_BENEFICIAL_OWNER_PATH,
          },
          {
            condition: true,
            path: FLOW_STEP_PATHS.APPLICANT_PATH,
          },
        ],
        extraProps: {
          defaultRole: STAKEHOLDER_ROLES_V2.ULTIMATE_BENEFICIAL_OWNER,
          cantHaveRoles: [
            STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT,
            STAKEHOLDER_ROLES_V2.PARTNER,
          ],
          roleConstraints: 'defaultRoleConstraints',
        },
        fieldsToFill: [
          {
            fieldName: 'stakeholders',
            roles: [STAKEHOLDER_ROLES_V2.ULTIMATE_BENEFICIAL_OWNER],
          },
        ],
      },
      {
        id: 'ceo',
        path: FLOW_STEP_PATHS.ARTIFICIAL_ULTIMATE_BENEFICIAL_OWNER_PATH,
        prerequisite: this.withGlobalPrerequisites([not('hasUltimateBeneficialOwner')]),
        Container: Ceo,
        back: FLOW_STEP_PATHS.ULTIMATE_BENEFICIAL_OWNER_PATH,
        next: FLOW_STEP_PATHS.APPLICANT_PATH,
        extraProps: {
          defaultRole: STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT,
          roleConstraints: {
            [STAKEHOLDER_ROLES_V2.CONTROL_OVER_MANAGEMENT]: {
              min: 1,
              max: 1,
            },
          },
        },
      },
      {
        id: 'applicant',
        path: FLOW_STEP_PATHS.APPLICANT_PATH,
        prerequisite: this.withGlobalPrerequisites([
          or(
            ['hasUltimateBeneficialOwner', 'hasCompanyDetails'],
            [
              'hasUltimateBeneficialOwner',
              'isEntityTypePartnership',
              'hasPartnershipDetails',
              'hasPartnerRole',
            ],
            'hasControlOverManagementStakeholders',
            'isEntityTypePublicListedCompany',
          ),
        ]),
        Container: Applicant,
        back: [
          {
            condition: 'hasControlOverManagementStakeholders',
            path: FLOW_STEP_PATHS.ARTIFICIAL_ULTIMATE_BENEFICIAL_OWNER_PATH,
          },
          {
            condition: not('isEntityTypePublicListedCompany'),
            path: FLOW_STEP_PATHS.ULTIMATE_BENEFICIAL_OWNER_PATH,
          },
          {
            condition: true,
            path: FLOW_STEP_PATHS.COMPANY_DETAILS_PATH,
          },
        ],
        next,
        extraProps: {
          defaultRole: STAKEHOLDER_ROLES_V2.APPLICANT,
          overrideStakeholders: true,
          roleConstraints: {
            [STAKEHOLDER_ROLES_V2.APPLICANT]: {
              min: 1,
            },
          },
        },
        fieldsToFill: [
          {
            fieldName: 'stakeholders',
            roles: [STAKEHOLDER_ROLES_V2.APPLICANT],
          },
        ],
      },
    ];
  }
}
